import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import { DISCLAIMER_TEXT } from "@/lib/crisisResources";
import { BottomNavigation } from "@/components/layout/BottomNavigation";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import axios from "axios";
import { toast } from "sonner";
import {
  TrendingUp,
  TrendingDown,
  Flame,
  Calendar,
  BarChart3,
  Target,
  Lightbulb,
  Lock,
  ChevronRight,
  ArrowLeft,
  AlertTriangle
} from "lucide-react";

const DAY_ABBREVS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function PatternsPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [patterns, setPatterns] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    if (token) {
      fetchPatterns();
    }
  }, [token]);

  const fetchPatterns = async () => {
    try {
      const response = await axios.get(`${API}/user/patterns`, {
        withCredentials: true
      });
      setPatterns(response.data);
    } catch (error) {
      console.error("Patterns error:", error);
      toast.error("Failed to load patterns");
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <BarChart3 className="w-12 h-12 text-primary/50 mx-auto mb-4 animate-pulse" />
          <p className="text-white/50">Loading your patterns...</p>
        </div>
      </div>
    );
  }

  // Locked state (not enough data)
  if (patterns && !patterns.unlocked) {
    return (
      <div className="min-h-screen bg-background pb-20">
        <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-white/5 px-4 py-4">
          <div className="max-w-2xl mx-auto flex items-center gap-4">
            <button 
              onClick={() => navigate(-1)}
              className="p-2 rounded-lg hover:bg-white/5 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-white/70" />
            </button>
            <h1 className="text-xl font-heading text-white">My Patterns</h1>
          </div>
        </header>

        <main className="px-4 py-8 max-w-2xl mx-auto">
          <div className="text-center py-12">
            <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-6">
              <Lock className="w-10 h-10 text-white/30" />
            </div>
            <h2 className="text-2xl font-heading text-white mb-3">
              Patterns Unlock Soon
            </h2>
            <p className="text-white/60 mb-8 max-w-sm mx-auto">
              {patterns.message}
            </p>
            
            {/* Progress indicator */}
            <div className="bg-white/5 rounded-2xl p-6 max-w-sm mx-auto">
              <div className="flex items-center justify-between mb-3">
                <span className="text-white/50 text-sm">Progress</span>
                <span className="text-white font-medium">
                  {patterns.days_with_data} / {patterns.days_required} days
                </span>
              </div>
              <Progress 
                value={(patterns.days_with_data / patterns.days_required) * 100} 
                className="h-2 mb-4"
              />
              <p className="text-primary text-sm">
                {patterns.days_until_unlock} more day{patterns.days_until_unlock !== 1 ? 's' : ''} of check-ins to unlock
              </p>
            </div>

            <Link
              to="/compass"
              className="inline-flex items-center gap-2 mt-8 px-6 py-3 bg-primary hover:bg-primary/90 text-white rounded-xl font-medium transition-colors"
            >
              Do Today's Check-in
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </main>

        <BottomNavigation />
      </div>
    );
  }

  // Unlocked state - show patterns
  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-white/5 px-4 py-4">
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg hover:bg-white/5 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-white/70" />
          </button>
          <div>
            <h1 className="text-xl font-heading text-white">My Patterns</h1>
            <p className="text-white/40 text-xs">Based on {patterns?.days_with_data || 0} days of data</p>
          </div>
        </div>
      </header>

      <main className="px-4 py-6 max-w-2xl mx-auto space-y-6">
        {/* Quick Stats Row */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="glass-card">
            <CardContent className="p-4 text-center">
              <Flame className="w-6 h-6 text-primary mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{patterns?.streaks?.current || 0}</p>
              <p className="text-white/40 text-xs">Current Streak</p>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="p-4 text-center">
              <Target className="w-6 h-6 text-primary mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{patterns?.overall?.follow_through_rate || 0}%</p>
              <p className="text-white/40 text-xs">Follow-through</p>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="p-4 text-center">
              <Calendar className="w-6 h-6 text-primary mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{patterns?.overall?.consistency_30_days || 0}%</p>
              <p className="text-white/40 text-xs">30-Day Consistency</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4">
          {[
            { id: "overview", label: "Overview", icon: BarChart3 },
            { id: "weekly", label: "By Day", icon: Calendar },
            { id: "patterns", label: "Patterns", icon: Lightbulb }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? "bg-primary text-white"
                  : "bg-white/5 text-white/60 hover:bg-white/10"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === "overview" && (
          <div className="space-y-4">
            {/* Follow-through breakdown */}
            <Card className="glass-card">
              <CardContent className="p-5">
                <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                  <Target className="w-4 h-4 text-primary" />
                  Follow-through Breakdown
                </h3>
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-white/70">Yes (followed through)</span>
                      <span className="text-green-400">{patterns?.overall?.yes_count || 0}</span>
                    </div>
                    <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-green-500 rounded-full transition-all"
                        style={{ 
                          width: `${patterns?.overall?.follow_through_rate || 0}%` 
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-white/70">No (didn't follow through)</span>
                      <span className="text-amber-400">{patterns?.overall?.no_count || 0}</span>
                    </div>
                    <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-amber-500 rounded-full transition-all"
                        style={{ 
                          width: `${100 - (patterns?.overall?.follow_through_rate || 0)}%` 
                        }}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Resistance Patterns */}
            {patterns?.resistance_patterns?.length > 0 && (
              <Card className="glass-card">
                <CardContent className="p-5">
                  <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-400" />
                    Tagged Patterns
                  </h3>
                  <div className="space-y-3">
                    {patterns.resistance_patterns.map((pattern, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <span className="text-white/70">{pattern.label}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-white/10 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-amber-500/70 rounded-full"
                              style={{ 
                                width: `${Math.min(100, (pattern.count / (patterns.overall?.no_count || 1)) * 100)}%` 
                              }}
                            />
                          </div>
                          <span className="text-white/50 text-sm w-8 text-right">{pattern.count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Streak Stats */}
            <Card className="glass-card">
              <CardContent className="p-5">
                <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                  <Flame className="w-4 h-4 text-primary" />
                  Streak Stats
                </h3>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-3xl font-bold text-white">{patterns?.streaks?.current || 0}</p>
                    <p className="text-white/40 text-xs">Current</p>
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-white">{patterns?.streaks?.longest || 0}</p>
                    <p className="text-white/40 text-xs">Longest</p>
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-white">{patterns?.streaks?.total_checkins || 0}</p>
                    <p className="text-white/40 text-xs">Total Check-ins</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === "weekly" && (
          <div className="space-y-4">
            {/* Day of Week Chart */}
            <Card className="glass-card">
              <CardContent className="p-5">
                <h3 className="text-white font-medium mb-4">Follow-through by Day</h3>
                <div className="flex items-end justify-between gap-2 h-40">
                  {patterns?.day_of_week?.patterns?.map((day, idx) => {
                    const height = day.follow_through_rate !== null 
                      ? Math.max(10, day.follow_through_rate) 
                      : 10;
                    const isBest = patterns?.day_of_week?.best_day?.day === day.day;
                    const isWorst = patterns?.day_of_week?.worst_day?.day === day.day;
                    
                    return (
                      <div key={idx} className="flex-1 flex flex-col items-center">
                        <div className="w-full flex flex-col items-center justify-end h-28">
                          {day.follow_through_rate !== null && (
                            <span className="text-xs text-white/50 mb-1">
                              {day.follow_through_rate}%
                            </span>
                          )}
                          <div 
                            className={`w-full rounded-t-lg transition-all ${
                              isBest ? 'bg-green-500' : 
                              isWorst ? 'bg-amber-500' : 
                              'bg-primary/70'
                            }`}
                            style={{ height: `${height}%` }}
                          />
                        </div>
                        <span className={`text-xs mt-2 ${
                          isBest ? 'text-green-400 font-medium' :
                          isWorst ? 'text-amber-400 font-medium' :
                          'text-white/50'
                        }`}>
                          {DAY_ABBREVS[idx]}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Best & Worst Days */}
            <div className="grid grid-cols-2 gap-3">
              {patterns?.day_of_week?.best_day && (
                <Card className="glass-card border-green-500/20">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-4 h-4 text-green-400" />
                      <span className="text-white/50 text-xs">Highest</span>
                    </div>
                    <p className="text-white font-medium">{patterns.day_of_week.best_day.day}</p>
                    <p className="text-green-400 text-sm">{patterns.day_of_week.best_day.follow_through_rate}% follow-through</p>
                  </CardContent>
                </Card>
              )}
              {patterns?.day_of_week?.worst_day && (
                <Card className="glass-card border-amber-500/20">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingDown className="w-4 h-4 text-amber-400" />
                      <span className="text-white/50 text-xs">Lowest</span>
                    </div>
                    <p className="text-white font-medium">{patterns.day_of_week.worst_day.day}</p>
                    <p className="text-amber-400 text-sm">{patterns.day_of_week.worst_day.follow_through_rate}% follow-through</p>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Daily Details */}
            <Card className="glass-card">
              <CardContent className="p-5">
                <h3 className="text-white font-medium mb-4">Daily Breakdown</h3>
                <div className="space-y-3">
                  {patterns?.day_of_week?.patterns?.map((day, idx) => (
                    <div key={idx} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                      <span className="text-white/70">{day.day}</span>
                      <div className="flex items-center gap-4">
                        <span className="text-white/40 text-sm">{day.total_checkins} check-ins</span>
                        <span className={`font-medium ${
                          day.follow_through_rate >= 70 ? 'text-green-400' :
                          day.follow_through_rate >= 50 ? 'text-white' :
                          day.follow_through_rate !== null ? 'text-amber-400' :
                          'text-white/30'
                        }`}>
                          {day.follow_through_rate !== null ? `${day.follow_through_rate}%` : '—'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === "patterns" && (
          <div className="space-y-4">
            {patterns?.insights?.length > 0 ? (
              <Card className="glass-card">
                <CardContent className="p-5 space-y-4">
                  {patterns.insights.map((item, idx) => (
                    <p key={idx} className="text-white/80 leading-relaxed">
                      {item.text}
                    </p>
                  ))}
                </CardContent>
              </Card>
            ) : (
              <div className="text-center py-12">
                <Lightbulb className="w-12 h-12 text-white/20 mx-auto mb-4" />
                <p className="text-white/50">No pattern data yet</p>
              </div>
            )}
          </div>
        )}
        
        {/* Scope Disclaimer - Always Visible */}
        <div className="mt-8 mb-4 px-4">
          <p className="text-xs text-center text-white/40">
            {DISCLAIMER_TEXT}
          </p>
        </div>
      </main>

      <BottomNavigation />
    </div>
  );
}
